import React from 'react'
import { Slate, Editable, ReactEditor } from 'slate-react'
import { BaseEditor, Descendant, Editor, Transforms } from 'slate'
import css from './richTextEditor.module.css'
import Toolbar from './components/Toolbar'
import { CustomElement, CustomText } from './richTextEditor.types'
import richTextEditorUtils from './richTextEditor.utils'
import { HistoryEditor } from 'slate-history'
import cn from './../../../../../utils/cn'
import RenderLeaf from './components/RenderLeaf'
import RenderElement from './components/RenderElement'

interface IProps {
  editor: BaseEditor & ReactEditor & HistoryEditor
  name: string
  placeHolder: string
  value: Descendant[]
  onChange: (val: Descendant[], name: string) => void
}

const RichTextEditor = (props: IProps) => {
  const editor = props.editor
  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    const key = event?.key?.toLowerCase()

    if (key.length === 1 && !event.ctrlKey && !event.metaKey) {
      event.preventDefault()

      const { selection } = editor
      if (!selection) return

      const [match] = Array.from(Editor.nodes(editor, { match: (n: CustomText) => n.type === 'parameter' }))

      if (match) {
        // Если курсор внутри параметра или перед ним
        const [node, path] = match

        // Вычисляем длину параметра, чтобы сдвинуть курсор за него
        const paramLength = (node as CustomText).text.length + 1

        const isBeforeParam = Editor.before(editor, selection, { unit: 'offset' }) === Editor.start(editor, path)

        if (isBeforeParam) {
          // Ставим курсор в конец параметра
          Transforms.move(editor, { distance: paramLength, unit: 'offset' })
        }

        // Вставляем текст после параметра
        Transforms.insertNodes(
          editor,
          { text: event.key, type: 'text', fontSize: 14 },
          { at: Editor.end(editor, path) }
        )
        requestAnimationFrame(() => Transforms.move(editor, { distance: 2, unit: 'offset' }))
      } else {
        // Обычная вставка текста
        Transforms.insertText(editor, event.key)
      }
    }

    if (key === 'enter') {
      event.preventDefault()

      const { selection } = editor
      if (!selection) return

      const [match] = Array.from(Editor.nodes(editor, { match: (n: CustomText) => n.type === 'parameter' }))

      // Если курсор находится внутри или перед параметром, сдвигаем его за параметр
      if (match) {
        const [node, path] = match
        const paramLength = (node as CustomText).text.length + 1

        const isBeforeParam = Editor.before(editor, selection, { unit: 'offset' }) === Editor.start(editor, path)
        if (isBeforeParam) {
          // Сдвигаем курсор за параметр
          Transforms.move(editor, { distance: paramLength, unit: 'offset' })
        }

        // Вставляем новый пустой текстовый узел после параметра (для новой строки)
        Transforms.insertNodes(editor, { type: 'text', text: '', fontSize: 14 })
      } else {
        // Вставляем новый пустой текстовый узел для новой строки
        Transforms.insertNodes(editor, { type: 'text', text: '', fontSize: 14 })
      }

      // Перемещаем курсор на новую строку
      // Transforms.move(editor, { distance: 1, unit: 'offset' })
      Transforms.insertNodes(editor, { children: [{ type: 'text', text: '', fontSize: 14 }] })
    }

    // Предотвращаем удаление параметров
    if (key === 'backspace' || key === 'delete') {
      const { selection } = editor
      if (selection) {
        const [match] = Array.from(Editor.nodes(editor, { match: (n: CustomText) => n.type === 'parameter' }))

        // Если мы находим параметр, и он пустой, разрешаем его удалить
        if (match) {
          const [node, path] = match
          if ((node as CustomText).text === '') {
            // Удаляем пустой параметр
            Transforms.removeNodes(editor, { at: path })
          } else {
            event.preventDefault()
          }
        }
      }
    }

    if (key === '$') {
      event.preventDefault()
    }
    if (key === 'b' && event?.ctrlKey) {
      richTextEditorUtils.toggleMark(editor, 'bold')
    }
    if (key === 'i' && event?.ctrlKey) {
      richTextEditorUtils.toggleMark(editor, 'italic')
    }
    if (key === 'u' && event?.ctrlKey) {
      richTextEditorUtils.toggleMark(editor, 'underline')
    }
    if (key === 'z' && event?.ctrlKey) {
      editor.undo()
    }
    if (key === 'y' && event?.ctrlKey) {
      editor.redo()
    }
  }

  const onChangeText = (newValue: Descendant[]) => {
    const filteredValue = newValue.filter((node: CustomElement) => node.children?.length)
    props.onChange(filteredValue as Descendant[], props.name)
  }

  return (
    <Slate editor={editor} initialValue={props.value} onChange={onChangeText} key={JSON.stringify(props.value)}>
      <Toolbar />
      <Editable
        placeholder={props.placeHolder || 'Введите текст'}
        className={css.wrapper}
        renderLeaf={RenderLeaf}
        renderElement={RenderElement}
        onKeyDown={onKeyDown}
        // autoFocus
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      />
      <div className={cn('text-nm', css.hint)}>
        Используйте «$1», «$2» и т. д. для добавления динамических параметров
      </div>
    </Slate>
  )
}

export default RichTextEditor
